import { gql } from '@apollo/client';

/** 产能管理策略：1 分拣产能管理（尾程 HUB 三池），2 尾程产能管理（派送 Warehouse） */
export const OrderQuotaStrategy = {
    CUSTOMER_PICKUP_HUB: 1,
    DEST_WAREHOUSE: 2,
} as const;

export type OrderQuotaStrategyType = (typeof OrderQuotaStrategy)[keyof typeof OrderQuotaStrategy];

/** 客户分级：1 高优，3 低优；不在分级名单中的客户为普通 */
export const CustomerTier = {
    HIGH: 1,
    LOW: 3,
} as const;

export type CustomerTierCode = (typeof CustomerTier)[keyof typeof CustomerTier];

/** 网格默认展示今日起 60 天（含今日共 61 列） */
export const GRID_DAYS = 61;

export interface OrderQuotaHubSummary {
    hubId: string;
    hubCode: string;
    hubName: string;
    /** 测试 HUB（供上线验证，排在最后）：客户分级文件中该列选填 */
    isTest: boolean;
    enabled: boolean;
    /** 分拣产能管理的基础产能，0 = 直接拦截 */
    hubDailyQuota: number;
    rowCount: number;
    configuredRowCount: number;
}

export interface OrderQuotaDailyOverride {
    date: string;
    dailyQuota: number;
}

export interface OrderQuotaRow {
    targetId: string;
    targetCode: string;
    targetName: string;
    /** 分拣产能管理的客户分级，尾程为空 */
    customerTier: CustomerTierCode | null;
    configured: boolean;
    dailyQuota: number | null;
    isPaused: boolean;
    overrides: OrderQuotaDailyOverride[];
}

export interface OrderQuotaDetail {
    strategyType: number;
    hubId: string;
    hubCode: string | null;
    hubName: string | null;
    enabled: boolean;
    /** 分拣产能管理的基础产能，0 = 直接拦截 */
    hubDailyQuota: number;
    transferReserve: number;
    floatCapacity: number;
    today: string;
    startDate: string;
    endDate: string;
    rows: OrderQuotaRow[];
}

export interface OrderQuotaImportError {
    lineNo: number;
    targetCode: string | null;
    messages: string[];
}

export interface OrderQuotaImportResult {
    total: number;
    validCount: number;
    newCount: number;
    updateCount: number;
    /** 已存在且与现有配置相同、不会写入的行数 */
    unchangedCount: number;
    ignoredPastDateColumns: number;
    errors: OrderQuotaImportError[];
    applied: boolean;
}

export interface OrderQuotaImportRowInput {
    lineNo: number;
    targetCode: string | null;
    dailyQuota: string | null;
    overrides: { date: string; value: string | null }[];
}

/** 客户在某个尾程 HUB 的每天默认值（高优为保障值、低优为上限），为空表示未填 */
export interface OrderQuotaTierHubValue {
    hubId: string;
    hubCode: string;
    dailyQuota: number | null;
}

export interface OrderQuotaCustomerTier {
    channelId: string;
    channelName: string;
    tier: CustomerTierCode;
    values: OrderQuotaTierHubValue[];
}

export interface OrderQuotaTierImportRowInput {
    lineNo: number;
    channelId: string | null;
    /** 高优 / 低优 / 普通（普通表示取消分级） */
    tier: string | null;
    /**
     * 各 HUB 的每天默认值：分级变化为高优 / 低优时每个正式 HUB 必填，分级不变时留空表示不修改；测试 HUB 选填。
     * hubCode 为表头中的 HUB（HUB 名称或分拣码），由后端识别
     */
    values: { hubCode: string; dailyQuota: string | null }[];
}

export const GET_ORDER_QUOTA_HUB_OVERVIEW = gql`
    query OnboardingOrderQuotaHubOverview($strategyType: Int!) {
        onboardingOrderQuotaHubOverview(strategyType: $strategyType) {
            hubId
            hubCode
            hubName
            isTest
            enabled
            hubDailyQuota
            rowCount
            configuredRowCount
        }
    }
`;

export const GET_ORDER_QUOTA_DETAIL = gql`
    query OnboardingOrderQuotaDetail($strategyType: Int!, $hubId: String!, $startDate: String, $days: Int!) {
        onboardingOrderQuotaDetail(strategyType: $strategyType, hubId: $hubId, startDate: $startDate, days: $days) {
            strategyType
            hubId
            hubCode
            hubName
            enabled
            hubDailyQuota
            transferReserve
            floatCapacity
            today
            startDate
            endDate
            rows {
                targetId
                targetCode
                targetName
                customerTier
                configured
                dailyQuota
                isPaused
                overrides {
                    date
                    dailyQuota
                }
            }
        }
    }
`;

export const SET_ORDER_QUOTA_SWITCH = gql`
    mutation SetOnboardingOrderQuotaSwitch($strategyType: Int!, $hubId: String!, $enabled: Boolean!) {
        setOnboardingOrderQuotaSwitch(strategyType: $strategyType, hubId: $hubId, enabled: $enabled) {
            enabled
            hubDailyQuota
        }
    }
`;

export const SET_ORDER_QUOTA_HUB_CAPACITY = gql`
    mutation SetOnboardingOrderQuotaHubCapacity($hubId: String!, $baseCapacity: Int!, $transferReserve: Int!, $floatCapacity: Int!) {
        setOnboardingOrderQuotaHubCapacity(
            hubId: $hubId
            baseCapacity: $baseCapacity
            transferReserve: $transferReserve
            floatCapacity: $floatCapacity
        ) {
            enabled
            hubDailyQuota
            transferReserve
            floatCapacity
        }
    }
`;

export const GET_ORDER_QUOTA_CUSTOMER_TIERS = gql`
    query OnboardingOrderQuotaCustomerTiers {
        onboardingOrderQuotaCustomerTiers {
            channelId
            channelName
            tier
            values {
                hubId
                hubCode
                dailyQuota
            }
        }
    }
`;

export const IMPORT_ORDER_QUOTA_CUSTOMER_TIERS = gql`
    mutation ImportOnboardingOrderQuotaCustomerTiers($rows: [OnboardingOrderQuotaTierImportRowInput!]!, $dryRun: Boolean!) {
        importOnboardingOrderQuotaCustomerTiers(rows: $rows, dryRun: $dryRun) {
            total
            validCount
            newCount
            updateCount
            unchangedCount
            ignoredPastDateColumns
            errors {
                lineNo
                targetCode
                messages
            }
            applied
        }
    }
`;

export const SET_ORDER_QUOTA_RULE_DAILY_QUOTA = gql`
    mutation SetOnboardingOrderQuotaRuleDailyQuota($strategyType: Int!, $hubId: String!, $targetId: String!, $dailyQuota: Int!) {
        setOnboardingOrderQuotaRuleDailyQuota(strategyType: $strategyType, hubId: $hubId, targetId: $targetId, dailyQuota: $dailyQuota) {
            targetId
            dailyQuota
            isPaused
        }
    }
`;

export const SET_ORDER_QUOTA_RULE_PAUSED = gql`
    mutation SetOnboardingOrderQuotaRulePaused($strategyType: Int!, $hubId: String!, $targetId: String!, $paused: Boolean!) {
        setOnboardingOrderQuotaRulePaused(strategyType: $strategyType, hubId: $hubId, targetId: $targetId, paused: $paused) {
            targetId
            dailyQuota
            isPaused
        }
    }
`;

export const SET_ORDER_QUOTA_DAILY_OVERRIDES = gql`
    mutation SetOnboardingOrderQuotaDailyOverrides(
        $strategyType: Int!
        $hubId: String!
        $targetId: String!
        $startDate: String!
        $endDate: String!
        $dailyQuota: Int
    ) {
        setOnboardingOrderQuotaDailyOverrides(
            strategyType: $strategyType
            hubId: $hubId
            targetId: $targetId
            startDate: $startDate
            endDate: $endDate
            dailyQuota: $dailyQuota
        )
    }
`;

export const IMPORT_ORDER_QUOTA = gql`
    mutation ImportOnboardingOrderQuota(
        $strategyType: Int!
        $hubId: String!
        $rows: [OnboardingOrderQuotaImportRowInput!]!
        $dryRun: Boolean!
    ) {
        importOnboardingOrderQuota(strategyType: $strategyType, hubId: $hubId, rows: $rows, dryRun: $dryRun) {
            total
            validCount
            newCount
            updateCount
            unchangedCount
            ignoredPastDateColumns
            errors {
                lineNo
                targetCode
                messages
            }
            applied
        }
    }
`;
